/**
 * Utilitaires d'accessibilité pour Ekose-Rx
 * Améliore l'expérience pour les utilisateurs avec des besoins spécifiques
 */

import { logger } from './logger';

// Types pour l'accessibilité
export interface A11yConfig {
  announcePageChanges: boolean;
  enableKeyboardNavigation: boolean;
  enableHighContrast: boolean;
  enableReducedMotion: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'extra-large';
}

export interface A11yAnnouncement {
  message: string;
  priority: 'polite' | 'assertive';
  delay?: number;
}

class AccessibilityManager {
  private config: A11yConfig;
  private announcer: HTMLElement | null = null;
  private focusTrap: HTMLElement[] = [];

  constructor() {
    this.config = {
      announcePageChanges: true,
      enableKeyboardNavigation: true,
      enableHighContrast: false,
      enableReducedMotion: false,
      fontSize: 'medium',
    };

    this.initializeAnnouncer();
    this.loadUserPreferences();
    this.setupGlobalKeyboardHandlers();
  }

  /**
   * Initialise l'élément d'annonce pour les lecteurs d'écran
   */
  private initializeAnnouncer(): void {
    this.announcer = document.createElement('div');
    this.announcer.setAttribute('aria-live', 'polite');
    this.announcer.setAttribute('aria-atomic', 'true');
    this.announcer.setAttribute('class', 'sr-only');
    this.announcer.style.cssText = `
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    `;
    document.body.appendChild(this.announcer);
  }

  /**
   * Annonce un message aux lecteurs d'écran
   */
  announce(announcement: A11yAnnouncement): void {
    if (!this.announcer || !this.config.announcePageChanges) return;

    const { message, priority, delay = 100 } = announcement;

    // Changer la priorité si nécessaire
    if (this.announcer.getAttribute('aria-live') !== priority) {
      this.announcer.setAttribute('aria-live', priority);
    }

    // Délai pour s'assurer que le lecteur d'écran capte le changement
    setTimeout(() => {
      if (this.announcer) {
        this.announcer.textContent = message;
        logger.debug(`A11y announcement: ${message}`);
      }
    }, delay);

    // Nettoyer après annonce
    setTimeout(() => {
      if (this.announcer) {
        this.announcer.textContent = '';
      }
    }, delay + 3000);
  }

  /**
   * Annonce les changements de page
   */
  announcePageChange(pageName: string): void {
    this.announce({
      message: `Navigation vers ${pageName}`,
      priority: 'polite',
    });
  }

  /**
   * Annonce les erreurs importantes
   */
  announceError(errorMessage: string): void {
    this.announce({
      message: `Erreur: ${errorMessage}`,
      priority: 'assertive',
    });
  }

  /**
   * Annonce les succès d'actions
   */
  announceSuccess(successMessage: string): void {
    this.announce({
      message: `Succès: ${successMessage}`,
      priority: 'polite',
    });
  }

  /**
   * Configure le focus trap pour les modales
   */
  setFocusTrap(container: HTMLElement): void {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    this.focusTrap = Array.from(focusableElements) as HTMLElement[];
    
    if (this.focusTrap.length > 0) {
      this.focusTrap[0].focus();
    }
  }

  /**
   * Supprime le focus trap
   */
  removeFocusTrap(): void {
    this.focusTrap = [];
  }

  /**
   * Gère la navigation au clavier dans un focus trap
   */
  private handleFocusTrapNavigation(event: KeyboardEvent): void {
    if (this.focusTrap.length === 0) return;

    const firstElement = this.focusTrap[0];
    const lastElement = this.focusTrap[this.focusTrap.length - 1];

    if (event.key === 'Tab') {
      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    if (event.key === 'Escape') {
      this.removeFocusTrap();
      // Retourner le focus à l'élément qui a ouvert la modale
      const trigger = document.querySelector('[data-modal-trigger]') as HTMLElement;
      if (trigger) {
        trigger.focus();
      }
    }
  }

  /**
   * Configure les gestionnaires de clavier globaux
   */
  private setupGlobalKeyboardHandlers(): void {
    document.addEventListener('keydown', (event) => {
      if (!this.config.enableKeyboardNavigation) return;

      // Gestion du focus trap
      this.handleFocusTrapNavigation(event);

      // Raccourcis clavier globaux
      if (event.altKey) {
        switch (event.key) {
          case '1':
            event.preventDefault();
            this.focusMainContent();
            break;
          case '2':
            event.preventDefault();
            this.focusNavigation();
            break;
          case 's':
            event.preventDefault();
            this.skipToContent();
            break;
        }
      }
    });
  }

  /**
   * Met le focus sur le contenu principal
   */
  private focusMainContent(): void {
    const main = document.querySelector('main, [role="main"], #main-content') as HTMLElement;
    if (main) {
      main.focus();
      this.announce({
        message: 'Focus sur le contenu principal',
        priority: 'polite',
      });
    }
  }

  /**
   * Met le focus sur la navigation
   */
  private focusNavigation(): void {
    const nav = document.querySelector('nav, [role="navigation"]') as HTMLElement;
    if (nav) {
      const firstLink = nav.querySelector('a, button') as HTMLElement;
      if (firstLink) {
        firstLink.focus();
        this.announce({
          message: 'Focus sur la navigation',
          priority: 'polite',
        });
      }
    }
  }

  /**
   * Passe au contenu principal (skip link)
   */
  private skipToContent(): void {
    const skipLink = document.querySelector('#skip-to-content') as HTMLElement;
    if (skipLink) {
      skipLink.click();
    } else {
      this.focusMainContent();
    }
  }

  /**
   * Applique le thème de contraste élevé
   */
  setHighContrast(enabled: boolean): void {
    this.config.enableHighContrast = enabled;
    
    if (enabled) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }

    this.saveUserPreferences();
    this.announce({
      message: `Contraste élevé ${enabled ? 'activé' : 'désactivé'}`,
      priority: 'polite',
    });
  }

  /**
   * Configure la réduction de mouvement
   */
  setReducedMotion(enabled: boolean): void {
    this.config.enableReducedMotion = enabled;
    
    if (enabled) {
      document.documentElement.classList.add('reduce-motion');
    } else {
      document.documentElement.classList.remove('reduce-motion');
    }

    this.saveUserPreferences();
    this.announce({
      message: `Mouvement réduit ${enabled ? 'activé' : 'désactivé'}`,
      priority: 'polite',
    });
  }

  /**
   * Configure la taille de police
   */
  setFontSize(size: A11yConfig['fontSize']): void {
    this.config.fontSize = size;
    
    // Supprimer les anciennes classes
    document.documentElement.classList.remove('font-small', 'font-medium', 'font-large', 'font-extra-large');
    
    // Ajouter la nouvelle classe
    document.documentElement.classList.add(`font-${size}`);

    this.saveUserPreferences();
    this.announce({
      message: `Taille de police changée à ${size}`,
      priority: 'polite',
    });
  }

  /**
   * Sauvegarde les préférences utilisateur
   */
  private saveUserPreferences(): void {
    try {
      localStorage.setItem('ekose-a11y-preferences', JSON.stringify(this.config));
    } catch (error) {
      logger.error('Erreur sauvegarde préférences a11y', error);
    }
  }

  /**
   * Charge les préférences utilisateur
   */
  private loadUserPreferences(): void {
    try {
      const saved = localStorage.getItem('ekose-a11y-preferences');
      if (saved) {
        const preferences = JSON.parse(saved) as Partial<A11yConfig>;
        this.config = { ...this.config, ...preferences };
        
        // Appliquer les préférences
        if (this.config.enableHighContrast) {
          this.setHighContrast(true);
        }
        if (this.config.enableReducedMotion) {
          this.setReducedMotion(true);
        }
        this.setFontSize(this.config.fontSize);
      }
    } catch (error) {
      logger.error('Erreur chargement préférences a11y', error);
    }
  }

  /**
   * Obtient la configuration actuelle
   */
  getConfig(): A11yConfig {
    return { ...this.config };
  }

  /**
   * Met à jour la configuration
   */
  updateConfig(updates: Partial<A11yConfig>): void {
    this.config = { ...this.config, ...updates };
    this.saveUserPreferences();
  }

  /**
   * Valide si un élément est accessible
   */
  validateElement(element: HTMLElement): string[] {
    const issues: string[] = [];

    // Vérifier les attributs ARIA requis
    if (element.tagName === 'BUTTON' && !element.getAttribute('aria-label') && !element.textContent?.trim()) {
      issues.push('Bouton sans label accessible');
    }

    if (element.tagName === 'IMG' && !element.getAttribute('alt')) {
      issues.push('Image sans texte alternatif');
    }

    if (element.tagName === 'INPUT' && !element.getAttribute('aria-label') && !element.getAttribute('aria-labelledby')) {
      issues.push('Input sans label accessible');
    }

    // Vérifier le contraste (simplifiée)
    const computedStyle = window.getComputedStyle(element);
    const color = computedStyle.color;
    const backgroundColor = computedStyle.backgroundColor;
    
    if (color && backgroundColor && color !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'rgba(0, 0, 0, 0)') {
      // TODO: Implémenter calcul de contraste réel
      // Pour l'instant, juste vérifier si les couleurs sont définies
    }

    return issues;
  }

  /**
   * Nettoie les ressources
   */
  destroy(): void {
    if (this.announcer) {
      document.body.removeChild(this.announcer);
      this.announcer = null;
    }
    this.focusTrap = [];
  }
}

// Instance singleton
export const a11yManager = new AccessibilityManager();

// Fonctions utilitaires
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
  a11yManager.announce({ message, priority });
};

export const announcePageChange = (pageName: string) => {
  a11yManager.announcePageChange(pageName);
};

export const announceError = (errorMessage: string) => {
  a11yManager.announceError(errorMessage);
};

export const announceSuccess = (successMessage: string) => {
  a11yManager.announceSuccess(successMessage);
};

export default a11yManager;
